import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import SEOHead from '../components/SEOHead'
import GalleryLightbox from '../components/GalleryLightbox'

export default function Tomrer() {
  const [galleryImages, setGalleryImages] = useState([])
  const [lightboxIndex, setLightboxIndex] = useState(null)
  const [projects, setProjects] = useState([])

  useEffect(() => {
    // Fetch gallery for tomrerarbejde
    fetch('https://api.dev.auxo.dk/public/v2/content/galleri')
      .then((res) => res.json())
      .then((data) => {
        if (data && data.items) {
          const match = data.items.find((item) => item.url_slug === 'tomrerarbejde' || item.tilknyttet_ydelse === 'tomrerarbejde')
          if (match && Array.isArray(match.billeder)) {
            setGalleryImages(match.billeder)
          }
        }
      })
      .catch(() => {})

    // Fetch related projects
    fetch('https://api.dev.auxo.dk/public/v2/content/projekter')
      .then((res) => res.json())
      .then((data) => {
        if (data && data.items) {
          const filtered = data.items.filter((p) => p.projektkategori === 'Tømrerarbejde' || p.relaterede_ydelser === 'tomrerarbejde')
          setProjects(filtered)
        }
      })
      .catch(() => {})
  }, [])

  return (
    <>
      <SEOHead
        title="Tømrerarbejde - Agstner Byg ApS"
        description="Agstner Byg ApS udfører professionelt tømrerarbejde i Vissenbjerg, Odense og på hele Fyn. Trækonstruktioner, vinduer, døre og tilbygninger. Få et tilbud."
      />

      {/* Hero */}
      <section className="relative py-24 bg-zinc-950 overflow-hidden border-b border-zinc-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/bac61bd8-01e8-4025-a9be-38664430d08c/image00003.jpeg"
            alt="Tømrerarbejde Agstner Byg ApS"
            className="w-full h-full object-cover object-top opacity-65"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/85 via-zinc-950/50 to-zinc-950/20" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-6">
            <div className="flex items-center gap-2 text-orange-500 font-display text-xs uppercase tracking-widest">
              <span className="w-6 h-0.5 bg-orange-500"></span>
              <span>YDELSE · AGSTNER BYG APS</span>
            </div>

            <h1 className="text-4xl sm:text-6xl font-display font-black text-white uppercase tracking-tight">
              Tømrerarbejde
            </h1>

            <p className="text-base sm:text-lg text-zinc-300 leading-relaxed">
              Professionelt tømrerarbejde udført med præcision og fokus på langtidsholdbare løsninger i træ.
            </p>

            <div className="pt-2 flex flex-wrap items-center gap-4">
              <a
                href="/kontakt"
                className="px-8 py-4 rounded-none bg-orange-500 hover:bg-orange-600 text-zinc-950 font-display font-bold text-xs uppercase tracking-widest transition-all border border-orange-500"
              >
                Få et tilbud
              </a>

              <a
                href="tel:+4552502543"
                className="px-6 py-4 rounded-none bg-zinc-900 hover:bg-zinc-800 text-orange-500 font-display font-bold text-xs uppercase tracking-widest border border-zinc-800 transition-all whitespace-nowrap"
              >
                <span className="whitespace-nowrap">Ring på 52 50 25 43</span>
              </a>

              <a
                href="#projekter-sektion"
                className="px-4 py-4 text-zinc-300 hover:text-white font-display font-bold text-xs uppercase tracking-widest transition-colors flex items-center gap-2"
              >
                <span>Se projekter</span>
                <i className="fa-solid fa-chevron-down text-xs text-orange-500" aria-hidden="true"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Intro & Details */}
      <section className="py-20 bg-zinc-900 text-white border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-7 space-y-6">
              <h2 className="text-2xl sm:text-4xl font-display font-bold text-white uppercase tracking-tight">
                Gennemarbejdet tømrerarbejde med blik for detaljen
              </h2>
              <div className="prose prose-invert max-w-none text-zinc-300 text-sm leading-relaxed space-y-4">
                <p>
                  Agstner Byg ApS tilbyder alt inden for professionelt tømrerarbejde til både private boliger og erhvervsejendomme. Vi fremstiller og monterer trækonstruktioner, døre og vinduer, vægge, gulve og udvendige træfacader med stor håndværksmæssig stolthed.
                </p>
                <p>
                  Uanset om du skal have bygget en ny træterrasse, skiftet vinduer eller udført komplekse konstruktionsopgaver, sikrer vi en tæt dialog og et gennemarbejdede resultat.
                </p>
              </div>

              {/* Work Areas Grid */}
              <div className="pt-4">
                <h3 className="text-sm font-display font-bold uppercase tracking-widest text-orange-500 mb-4">
                  Vi udfører blandt andet:
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-zinc-300 font-mono uppercase">
                  {[
                    'Generelt tømrerarbejde',
                    'Trækonstruktioner',
                    'Indvendigt arbejde',
                    'Udvendigt arbejde',
                    'Døre og vinduer',
                    'Tilbygninger',
                    'Ombygninger',
                    'Specialopgaver i træ'
                  ].map((task, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3.5 bg-zinc-950 border border-zinc-800">
                      <i className="fa-solid fa-check text-orange-500" aria-hidden="true"></i>
                      <span className="font-bold text-white">{task}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="border border-zinc-800 bg-zinc-950 p-0">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/bac61bd8-01e8-4025-a9be-38664430d08c/image00002.jpeg"
                  alt="Tømrerarbejde trækonstruktion"
                  className="w-full h-[450px] object-cover"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {galleryImages.length > 0 && (
        <section className="py-20 bg-zinc-950 border-b border-zinc-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8 border-b border-zinc-800 pb-4">
              <span className="text-xs font-display font-bold uppercase tracking-widest text-orange-500">BILLEDGALLERI</span>
              <h2 className="text-2xl sm:text-3xl font-display font-bold text-white uppercase mt-1">
                Galleri over tømrerarbejde
              </h2>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {galleryImages.map((imgUrl, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setLightboxIndex(idx)}
                  className="group relative h-48 border border-zinc-800 focus:outline-none focus:border-orange-500 overflow-hidden"
                >
                  <img
                    src={imgUrl}
                    alt={`Tømrerarbejde ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-zinc-950/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                    <i className="fa-solid fa-magnifying-glass-plus text-xl text-orange-500" aria-hidden="true"></i>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Projects Section */}
      <section id="projekter-sektion" className="py-20 bg-zinc-900 text-white border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-10 pb-4 border-b border-zinc-800">
            <div>
              <span className="text-xs font-display font-bold uppercase tracking-widest text-orange-500">PROJEKTER</span>
              <h2 className="text-2xl sm:text-3xl font-display font-bold text-white uppercase mt-1">
                Tømrerprojekter
              </h2>
            </div>
            <Link to="/projekter" className="text-xs font-display font-bold uppercase tracking-widest text-orange-500 hover:underline">
              Se alle projekter →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((proj) => (
              <div key={proj.id} className="bg-zinc-950 border border-zinc-800 p-5 group hover:border-orange-500 transition-all">
                <img
                  src={Array.isArray(proj.hovedbillede) ? proj.hovedbillede[0] : ''}
                  alt={proj.projektnavn}
                  className="w-full h-48 object-cover border border-zinc-800 mb-4"
                />
                <h3 className="font-display font-bold text-white text-lg uppercase group-hover:text-orange-500 transition-colors">{proj.projektnavn}</h3>
                <p className="text-xs font-mono text-orange-500 uppercase mt-1">{proj.omraade}</p>
                <p className="text-xs text-zinc-400 mt-2 line-clamp-2 leading-relaxed">{proj.kort_beskrivelse}</p>
                <div className="mt-4 pt-3 border-t border-zinc-800 flex justify-between items-center">
                  <Link to={`/projekter/${proj.url_slug}`} className="text-xs font-display font-bold uppercase tracking-widest text-orange-500 hover:underline">
                    Læs projekt →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Relaterede ydelser */}
      <section className="py-12 bg-zinc-950 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-xs font-display font-bold text-orange-500 uppercase tracking-widest mb-6">Relaterede ydelser:</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link
              to="/tag"
              className="p-5 bg-zinc-900 border border-zinc-800 hover:border-orange-500 flex items-center justify-between group transition-all"
            >
              <div className="flex items-center gap-4">
                <i className="fa-solid fa-house-chimney text-orange-500 text-xl" aria-hidden="true"></i>
                <div>
                  <h4 className="font-display font-bold text-white uppercase group-hover:text-orange-500 transition-colors">Tagarbejde</h4>
                  <p className="text-xs text-zinc-400">Nyt tag, tagrenovering og undertag</p>
                </div>
              </div>
              <i className="fa-solid fa-chevron-right text-xs text-zinc-500 group-hover:text-orange-500" aria-hidden="true"></i>
            </Link>

            <Link
              to="/renovering"
              className="p-5 bg-zinc-900 border border-zinc-800 hover:border-orange-500 flex items-center justify-between group transition-all"
            >
              <div className="flex items-center gap-4">
                <i className="fa-solid fa-screwdriver-wrench text-orange-500 text-xl" aria-hidden="true"></i>
                <div>
                  <h4 className="font-display font-bold text-white uppercase group-hover:text-orange-500 transition-colors">Renovering</h4>
                  <p className="text-xs text-zinc-400">Boligrenovering og modernisering</p>
                </div>
              </div>
              <i className="fa-solid fa-chevron-right text-xs text-zinc-500 group-hover:text-orange-500" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <GalleryLightbox
          images={galleryImages}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  )
}
